/**
 * {deliveryNote}の画面 /delivery-notes/:id（#266、docs/96 v0.2・Figma 257:508）。見積書のエディタ（Estimate.tsx の Editor）を{order}・{paymentNotice}と同じ形で使う
 *   頭（帳票の型 案B）：1段目＝‹・自動保存・↶ ↷・発行（下書き）／請求書作成（発行済・未請求）・⋯、2段目＝{deliveryNote}・件名・状態｜合計、
 *   3段目＝宛先・納品日・納品先・受領日・No.・{project}・元（見積書）
 *   ・税を確定する書類が請求書の{customer}は、合計の欄に「消費税は請求書にて」（紙も同じ）。納品書の{customer}は税率ごとの内訳と税額
 *   ・受領日は社内の記録（紙に出さない）＝発行のあとでも入れられる
 *   ・頭の自動保存は共通の仕組み（lib/saveQueue.tsx）。明細と同じ版を1本ずつ送る（409 DELIVERY_NOTE_STALE）
 *   ・スマホ版と 767px 以下は閲覧のみ
 * {acceptance}（検収書。#266 1b、docs/96 §14・Figma ⑤）も同じ画面（/acceptances/:id。kind acceptance）：3段目＝宛先・発行日・検収日・検収者・（検査期限）・No.・{project}・元（{deliveryNote}）、
 *   主ボタンは発行のあと「送付」、⋯ に 書面で検収・リンクコピー・PDF・取消。{deliveryNote}の発行のあとは ⋯ に 書面で受領・リンクコピー・{acceptance}作成（スイッチがオンのとき）、
 *   3段目の右にスイッチ「{acceptance}」（使う機能「{acceptance}」の会社だけ。既定は{customer}の設定）。受領日・検収日は記録（電子・書面）で入る＝欄は表示だけ
 */
import { ACCEPTANCE_MSG, CLOSING_MSG, DELIVERY_NOTE_MSG, NOTE_PATH, saveGateText, type AcceptanceLinkResult, type Company, type DeliveryNote, type DeliveryNoteIssueResult, type LineInput, type NoteDocKind, type SheetInput, type UnissueResult, UNISSUED_MSG, unissuedReissue, unissuedTitle } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { ME_KEY, useMe } from "../../auth.js";
import { DeleteDraftDialog } from "../../components/DeleteDraftDialog.jsx";
import { namePageNow } from "../../components/BackButton.jsx";
import { docShort } from "../../components/AreaIcon.jsx";
import { DocField, subjWidth } from "../../components/DocHead.jsx";
import { DateInput } from "../../components/DateInput.jsx";
import { HeadActions, type HeadAction } from "../../components/HeadActions.jsx";
import { UnissueDialog } from "../../components/UnissueDialog.jsx";
import { ErrorText, Field, Help, Switch, useToast } from "../../components/ui.jsx";
import { uses } from "../../core/uses.js";
import { t } from "../../labels.js";
import { dayJst, mdSlash, todayJst, yen } from "../../lib/num.js";
import { useNarrow } from "../../lib/useNarrow.js";
import { currentView } from "../../lib/view.js";
import { useSaveQueue } from "../../lib/saveQueue.jsx";
import { Editor, type InvoiceBackend } from "../Estimate.jsx";
import { BundleInvoiceDialog } from "./BundleInvoiceDialog.jsx";

type Head = { subject: string; issueDate: string; siteAddress: string; notes: string };
const headOf = (n: DeliveryNote): Head => ({ subject: n.revision.subject, issueDate: n.revision.issueDate, siteAddress: n.revision.siteAddress, notes: n.revision.notes });
/** 画面のデータの鍵（{deliveryNote}＝delivery-note・{acceptance}＝acceptance） */
export const noteQueryKey = (kind: NoteDocKind) => (kind === "acceptance" ? "acceptance" : "delivery-note");
/** 文の {deliveryNote} を検収書では {acceptance} に */
const kmsg = (kind: NoteDocKind, text: string) => (kind === "acceptance" ? text.replaceAll("{deliveryNote}", "{acceptance}") : text);

export function DeliveryNotePage({ kind = "deliveryNote" }: { kind?: NoteDocKind }) {
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: [noteQueryKey(kind), id], queryFn: () => api<DeliveryNote>("GET", `${NOTE_PATH[kind]}/${id}`), retry: false });
  if (q.isPending) return <p className="hint">読み込んでいます…</p>;
  if (q.isError) {
    return (
      <div className="soonbox">
        <ErrorText>{messageOf(q.error)}</ErrorText>
        <p>
          <Link className="btn text" to={kind === "acceptance" ? "/delivery-notes?tab=acceptance" : "/delivery-notes"}>{t(DELIVERY_NOTE_MSG.list)}</Link>
        </p>
      </div>
    );
  }
  // 発行・取消しで作り直す
  return <NoteEditor key={`${q.data.id}:${q.data.status}:${q.data.revision.id}`} initial={q.data} />;
}

/** 状態のチップ（一覧と同じ言葉）：下書き（番号ありも。#278）／発行済 MM/DD／請求済。1b：受領・検収のチップ（未確認／受領済／検収済） */
function stateChip(n: DeliveryNote) {
  if (n.status === "draft")
    return (
      <span className="chip quiet" data-testid="dn-status" title={n.documentNo ? unissuedTitle(n.documentNo) : undefined}>
        {n.documentNo ? UNISSUED_MSG.chip : "下書き"}
      </span>
    );
  const issued = n.invoice ? (
    <span className="chip ai" data-testid="dn-status">請求済</span>
  ) : (
    <span className="chip ai" data-testid="dn-status">発行済{n.issued ? ` ${mdSlash(dayJst(n.issued.at))}` : ""}</span>
  );
  const rec = n.record ? (
    <span className="chip ai" data-testid="dn-record-chip" title={n.record.recordNo ? `記録番号 ${n.record.recordNo}` : n.record.method === "paper" ? "書面" : undefined}>
      {n.kind === "acceptance" ? ACCEPTANCE_MSG.accepted : ACCEPTANCE_MSG.received} {mdSlash(n.record.on)}
    </span>
  ) : n.kind === "acceptance" ? (
    <span className="chip quiet" data-testid="dn-record-chip">{ACCEPTANCE_MSG.unconfirmed}</span>
  ) : null;
  return (
    <>
      {issued}
      {rec}
    </>
  );
}

function NoteEditor({ initial }: { initial: DeliveryNote }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const { toast, node: toastNode } = useToast();
  const narrow = useNarrow();
  const phone = narrow || currentView() === "phone";
  const draft = initial.status === "draft";
  const readOnly = !draft || phone;
  const kind: NoteDocKind = initial.kind ?? "deliveryNote";
  const isAc = kind === "acceptance";
  const P = NOTE_PATH[kind];
  const QK = noteQueryKey(kind);
  const title = isAc ? initial.names.acceptance || t("{acceptance}") : initial.names.deliveryNote;
  const cur = (qc.getQueryData([QK, initial.id]) as DeliveryNote | undefined) ?? initial;
  const [head, setHead] = useState<Head>(() => headOf(initial));

  /* ---- 版：明細（Editor）と頭は同じ PUT を別々の列で送る。版は1つ・1本ずつ ---- */
  const ver = useRef(initial.updatedAt);
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const serial = <T,>(fn: () => Promise<T>): Promise<T> => {
    const p = chain.current.then(fn, fn);
    chain.current = p.catch(() => undefined);
    return p;
  };
  const staleRef = useRef(false);
  const openStale = useRef<() => void>(() => undefined);
  const headBase = useRef<Head>(headOf(initial));
  const latest = useRef(head);
  latest.current = head;
  const hq = useSaveQueue({
    send: async () => {
      const h = latest.current;
      // 頭は下書きだけ（1b：受領日・検収日は記録＝書面で受領・書面で検収の窓／受領・検収のページで入る）
      if (!draft) return;
      const body = { subject: h.subject, issueDate: h.issueDate, siteAddress: h.siteAddress, notes: h.notes };
      const n = await serial(() => api<DeliveryNote>("PUT", `${P}/${initial.id}`, { ...body, version: ver.current }));
      ver.current = n.updatedAt;
      headBase.current = headOf(n);
      qc.setQueryData([QK, initial.id], n);
      void qc.invalidateQueries({ queryKey: ["delivery-notes"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && (err.code === "DELIVERY_NOTE_STALE" || err.code === "ACCEPTANCE_STALE")) {
        staleRef.current = true;
        openStale.current();
        return { msg: DELIVERY_NOTE_MSG.stale, then: "stop" };
      }
    },
    blocked: () => staleRef.current,
  });
  const setH = (patch: Partial<Head>, immediate = false) => {
    setHead((h) => ({ ...h, ...patch }));
    latest.current = { ...latest.current, ...patch };
    hq.mark(immediate);
  };
  const linesReady = useRef<() => Promise<string | null>>(async () => null);
  const register = useCallback((f: () => Promise<string | null>) => {
    linesReady.current = f;
  }, []);
  const readyAll = async (): Promise<string | null> => (await linesReady.current()) ?? (await hq.ready());

  /* ---- 発行 ---- */
  const needsCompany = !!me.data?.tenant.needsCompanyName;
  const [dlg, setDlg] = useState<{ company: string; busy: boolean; error?: string | undefined; separate?: boolean } | null>(null);
  const openIssue = async () => {
    const why = await readyAll();
    if (why != null) {
      toast(saveGateText("issue", why), undefined, "error");
      return;
    }
    let company = "";
    if (needsCompany) {
      try {
        company = (await api<Company>("GET", "/company")).name;
      } catch {
        /* 空のまま */
      }
    }
    setDlg({ company, busy: false });
  };
  const doIssue = async () => {
    if (!dlg) return;
    if (needsCompany && !dlg.company.trim()) {
      setDlg({ ...dlg, error: DELIVERY_NOTE_MSG.companyRequired });
      return;
    }
    setDlg({ ...dlg, busy: true, error: undefined });
    try {
      const why = await readyAll();
      if (why != null) {
        setDlg({ ...dlg, busy: false, error: saveGateText("issue", why) });
        return;
      }
      const r = await api<DeliveryNoteIssueResult>("POST", `${P}/${initial.id}/issue`, { ...(needsCompany ? { company: { name: dlg.company.trim() } } : {}), ...(dlg.separate ? { separate: true } : {}), version: ver.current });
      setDlg(null);
      hq.discard();
      void qc.invalidateQueries({ queryKey: ME_KEY });
      void qc.invalidateQueries({ queryKey: ["delivery-notes"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      void qc.invalidateQueries({ queryKey: ["delivery-state"] });
      const notice = t(DELIVERY_NOTE_MSG.issued).replace("{deliveryNote}", title).replace("{no}", r.documentNo);
      if (!phone) {
        // 発行したら紙の画面（送付）へ
        void qc.invalidateQueries({ queryKey: [QK, initial.id] });
        namePageNow(title, docShort(title, r.documentNo)); // 送信の画面の戻るボタンに番号（移る前に。読み直しを待たない）
        nav(`${P}/${initial.id}/paper`, { state: { notice, noticeTestId: "issued-notice" } });
        return;
      }
      await qc.invalidateQueries({ queryKey: [QK, initial.id] });
      toast(notice, undefined, "success", "issued-notice");
    } catch (e) {
      setDlg((d) => (d ? { ...d, busy: false, error: messageOf(e) } : d));
    }
  };

  /* ---- 取消し・削除・請求書作成 ---- */
  const [askDelete, setAskDelete] = useState(false);
  const [unissueDlg, setUnissueDlg] = useState<{ busy: boolean; error?: string | undefined } | null>(null);
  const [bundle, setBundle] = useState(false);
  const doUnissue = async () => {
    setUnissueDlg({ busy: true });
    try {
      const r = await api<UnissueResult>("POST", `${P}/${initial.id}/unissue`, {});
      setUnissueDlg(null);
      toast(t(DELIVERY_NOTE_MSG.unissued).replace("{deliveryNote}", title).replace("{no}", r.documentNo), undefined, "success", "unissued-notice");
      void qc.invalidateQueries({ queryKey: ["delivery-notes"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      await qc.invalidateQueries({ queryKey: [QK, initial.id] });
    } catch (e) {
      setUnissueDlg({ busy: false, error: messageOf(e) });
    }
  };
  // 削除したら元の見積の画面（見積から作ったとき）か一覧へ。窓は components/DeleteDraftDialog（見積書と同じ形）
  const deleteDraft = async () => {
    await api<void>("DELETE", `${P}/${initial.id}`);
    hq.discard();
    void qc.invalidateQueries({ queryKey: ["delivery-notes"] });
    void qc.invalidateQueries({ queryKey: ["delivery-state"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
    setAskDelete(false);
    nav(isAc ? (initial.sourceNote ? `/delivery-notes/${initial.sourceNote.id}` : "/delivery-notes?tab=acceptance") : initial.sourceQuote ? `/estimates/${initial.sourceQuote.id}` : "/delivery-notes", { replace: true, state: { notice: t(kmsg(kind, DELIVERY_NOTE_MSG.deleted)) } });
  };

  /* ---- 受領・検収（1b）：書面の窓・リンクコピー・{acceptance}作成・スイッチ ---- */
  const [paperDlg, setPaperDlg] = useState<{ on: string; by: string; busy: boolean; error?: string | undefined } | null>(null);
  const word = isAc ? "検収" : "受領";
  const doPaper = async () => {
    if (!paperDlg) return;
    if (!paperDlg.on) return setPaperDlg({ ...paperDlg, error: "日付を入れてください" });
    setPaperDlg({ ...paperDlg, busy: true, error: undefined });
    try {
      const n = await api<DeliveryNote>("POST", `${P}/${initial.id}/paper-record`, { on: paperDlg.on, ...(isAc && paperDlg.by.trim() ? { by: paperDlg.by.trim() } : {}) });
      setPaperDlg(null);
      ver.current = n.updatedAt;
      qc.setQueryData([QK, initial.id], n);
      void qc.invalidateQueries({ queryKey: ["delivery-notes"] });
      toast(ACCEPTANCE_MSG.recorded.replace("{word}", word), undefined, "success", "record-notice");
    } catch (e) {
      setPaperDlg((d) => (d ? { ...d, busy: false, error: messageOf(e) } : d));
    }
  };
  const copyLink = async () => {
    try {
      const r = await api<AcceptanceLinkResult>("POST", `${P}/${initial.id}/ack-link`, {});
      ver.current = r.updatedAt;
      await navigator.clipboard.writeText(r.url);
      void qc.invalidateQueries({ queryKey: [QK, initial.id] });
      toast(ACCEPTANCE_MSG.linkCopied.replace("{word}", word), undefined, "success", "link-copied");
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const makeAcceptance = async () => {
    try {
      const a = await api<DeliveryNote>("POST", "/acceptances", { fromDeliveryNoteId: initial.id });
      void qc.invalidateQueries({ queryKey: [QK, initial.id] });
      void qc.invalidateQueries({ queryKey: ["delivery-notes"] });
      nav(`/acceptances/${a.id}`);
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const usesAc = uses(me.data, "acceptance");
  const sw = cur.acceptanceSwitch;
  const [swOn, setSwOn] = useState<boolean>(!!sw?.on);
  const setSwitch = async (on: boolean) => {
    setSwOn(on);
    try {
      const n = await api<DeliveryNote>("PUT", `${P}/${initial.id}`, { attachAcceptance: on, version: ver.current });
      ver.current = n.updatedAt;
      qc.setQueryData([QK, initial.id], n);
    } catch (e) {
      setSwOn(!on);
      toast(messageOf(e), undefined, "error");
    }
  };
  const recordDone = cur.record?.method === "electronic";

  const paperUrl = `${P}/${initial.id}/paper`;
  const actions: HeadAction[] = draft
    ? [
        { label: DELIVERY_NOTE_MSG.pdfDraft, onSelect: () => void readyAll().then((why) => (why == null ? nav(paperUrl) : toast(saveGateText("pdf", why), undefined, "error"))), testId: "dn-pdf-draft", group: "doc" as const },
        { label: DELIVERY_NOTE_MSG.deleteDraft, onSelect: () => setAskDelete(true), testId: "dn-delete", group: "danger" as const },
      ]
    : [
        ...(cur.canBill && !phone ? [{ label: DELIVERY_NOTE_MSG.makeInvoice, onSelect: () => setBundle(true), testId: "dn-make-invoice", rank: 10 }] : []),
        ...(cur.invoice ? [{ label: cur.invoice.documentNo ? `請求書 ${cur.invoice.documentNo} 表示` : "請求書下書き表示", to: `/invoices/${cur.invoice.id}`, testId: "dn-invoice-link", rank: 20 }] : []),
        // {acceptance}（Figma ⑤-2）：主ボタンは「送付」
        { label: "送付", to: paperUrl, testId: "dn-send", ...(isAc ? { rank: 10 } : { group: "doc" as const }) },
        { label: "PDF 表示", to: paperUrl, testId: "dn-paper", rank: 30, group: "doc" as const },
        // 1b：書面で受領・書面で検収（電子の記録のあとは出さない）・リンクコピー（受領・検収のページ）・{acceptance}作成（スイッチがオン）
        ...(!phone && !recordDone ? [{ label: isAc ? ACCEPTANCE_MSG.paperAccept : ACCEPTANCE_MSG.paperReceive, onSelect: () => setPaperDlg({ on: todayJst(), by: isAc ? (cur.client.contact?.name ?? "") : "", busy: false }), testId: "dn-paper-record", group: "state" as const }] : []),
        ...(!phone && !recordDone ? [{ label: ACCEPTANCE_MSG.linkCopy, onSelect: () => void copyLink(), testId: "dn-link-copy", group: "doc" as const }] : []),
        ...(!isAc && !phone && usesAc && sw?.on ? [{ label: t(ACCEPTANCE_MSG.create), onSelect: () => void makeAcceptance(), testId: "dn-make-acceptance", group: "state" as const }] : []),
        ...(cur.canUnissue ? [{ label: DELIVERY_NOTE_MSG.unissue, onSelect: () => setUnissueDlg({ busy: false }), testId: "dn-unissue", group: "state" as const }] : []),
        ...(cur.unissueBlockedBy === "billed" ? [{ label: DELIVERY_NOTE_MSG.unissue, disabled: true, note: t(DELIVERY_NOTE_MSG.unissueBilled), testId: "dn-unissue", group: "state" as const }] : []),
        ...(cur.unissueBlockedBy === "record" ? [{ label: DELIVERY_NOTE_MSG.unissue, disabled: true, note: ACCEPTANCE_MSG.unissueHasRecord, testId: "dn-unissue", group: "state" as const }] : []),
      ];

  const backend: InvoiceBackend = {
    doc: {
      kind: isAc ? "acceptance" : "delivery_note",
      title,
      queryKey: QK,
      listKey: "delivery-notes",
      back: isAc
        ? initial.sourceNote
          ? { to: `/delivery-notes/${initial.sourceNote.id}`, label: `${initial.names.deliveryNote} ${initial.sourceNote.displayNo ?? ""}`.trim() }
          : { to: "/delivery-notes?tab=acceptance", label: `${title}一覧` }
        : initial.sourceQuote
          ? { to: `/estimates/${initial.sourceQuote.id}`, label: `見積書 ${initial.sourceQuote.displayNo ?? ""}`.trim() }
          : { to: "/delivery-notes", label: `${title}一覧` },
      labor: false,
    },
    save: (body: { sheets: SheetInput[]; lines: LineInput[] }) =>
      serial(async () => {
        const n = await api<DeliveryNote>("PUT", `${P}/${initial.id}`, { ...body, version: ver.current });
        ver.current = n.updatedAt;
        return n;
      }),
    version: () => ver.current,
    reload: () => api<DeliveryNote>("GET", `${P}/${initial.id}`),
    stale: () => {
      staleRef.current = true;
    },
    registerStale: (o) => {
      openStale.current = o;
    },
    synced: (e) => {
      const theirs = headOf(e as DeliveryNote);
      const base = headBase.current;
      const mine = latest.current;
      const merged: Head = { ...theirs };
      for (const k of Object.keys(theirs) as Array<keyof Head>) {
        if (JSON.stringify(mine[k]) !== JSON.stringify(base[k])) (merged as Record<keyof Head, unknown>)[k] = mine[k];
      }
      ver.current = e.updatedAt;
      headBase.current = theirs;
      latest.current = merged;
      setHead(merged);
      staleRef.current = false;
      if (JSON.stringify(merged) !== JSON.stringify(theirs)) hq.mark(true);
      else {
        hq.discard();
        hq.setState((x) => (x.kind === "error" ? { kind: "idle" } : x));
      }
    },
    register,
    actions: null,
    // 頭（docs/94 B-4 案B）：3段目＝宛先・納品日・納品先・受領日・No.・{project}・元
    docHead: {
      kind: "deliveryNotes",
      kindLabel: title,
      title: (
        <input id="dn_subj" className="dh-subj" style={subjWidth(head.subject)} aria-label={DELIVERY_NOTE_MSG.colSubject} placeholder={DELIVERY_NOTE_MSG.colSubject} value={head.subject} readOnly={readOnly} onChange={(e) => setH({ subject: e.target.value })} onKeyDown={(e) => e.key === "Enter" && e.preventDefault()} data-testid="dn-subject" />
      ),
      primary: (
        <span className="hd-acts" data-testid="head-actions">
          {draft && !phone ? (
            <button className="btn primary" type="button" onClick={() => void openIssue()} data-testid="dn-issue">
              {DELIVERY_NOTE_MSG.issue}
            </button>
          ) : null}
          <HeadActions actions={actions} label={t(DELIVERY_NOTE_MSG.stateMenu).replace("{deliveryNote}", title)} part="main" />
        </span>
      ),
      menu: <HeadActions actions={actions} label={t(DELIVERY_NOTE_MSG.stateMenu).replace("{deliveryNote}", title)} part="more" />,
      meta: (
        <>
          <DocField label="宛先">
            <span className="dh-to">
              <b data-testid="dn-client">{initial.client.name}</b> <span className="hon">{initial.client.honorific || "御中"}</span>
            </span>
          </DocField>
          <DocField label={isAc ? "発行日" : DELIVERY_NOTE_MSG.deliveryDate}>
            <DateInput inline label={isAc ? "発行日" : DELIVERY_NOTE_MSG.deliveryDate} ariaLabel={isAc ? "発行日" : DELIVERY_NOTE_MSG.deliveryDate} value={head.issueDate} readOnly={readOnly} clearable={false} onChange={(v) => v && setH({ issueDate: v }, true)} testId="dn-date" />
          </DocField>
          {isAc ? (
            <>
              {/* 検収日・検収者は記録（受領・検収のページ・書面で検収の窓）で入る＝表示だけ（Figma ⑤-1） */}
              <DocField label={ACCEPTANCE_MSG.acceptedOn}>
                <span className={cur.revision.acceptedOn ? "num" : "num muted"} data-testid="dn-accepted-on">{cur.revision.acceptedOn ? cur.revision.acceptedOn.replaceAll("-", "/") : "未入力"}</span>
              </DocField>
              <DocField label={ACCEPTANCE_MSG.acceptedBy}>
                <span className={cur.revision.acceptedBy ? undefined : "muted"} data-testid="dn-accepted-by">{cur.revision.acceptedBy || "未入力"}</span>
              </DocField>
              {cur.inspectUntil ? (
                <DocField label={<span>{ACCEPTANCE_MSG.inspectUntil}<Help label={ACCEPTANCE_MSG.inspectUntil} text={t(ACCEPTANCE_MSG.inspectHelp)} testId="help-dn-inspect" /></span>}>
                  <span className="num" data-testid="dn-inspect-until">{cur.inspectUntil.replaceAll("-", "/")}</span>
                </DocField>
              ) : null}
            </>
          ) : (
            <>
              <DocField label={<label htmlFor="dn_place">{DELIVERY_NOTE_MSG.deliveryPlace}</label>}>
                <input id="dn_place" className="inp dn-place" value={head.siteAddress} readOnly={readOnly} onChange={(e) => setH({ siteAddress: e.target.value })} data-testid="dn-place" />
              </DocField>
              {/* 受領日は記録（受領・検収のページ・書面で受領の窓）で入る＝表示だけ（1b） */}
              <DocField label={<span>{DELIVERY_NOTE_MSG.receivedOn}<Help label={DELIVERY_NOTE_MSG.receivedOn} text={t(DELIVERY_NOTE_MSG.receivedOnHelp)} testId="help-dn-received" /></span>}>
                <span className={cur.revision.receivedOn ? "num" : "num muted"} data-testid="dn-received">{cur.revision.receivedOn ? cur.revision.receivedOn.replaceAll("-", "/") : "未入力"}</span>
              </DocField>
            </>
          )}
          <DocField label="No.">
            <span className="num" data-testid="dn-no">{initial.displayNo ?? initial.documentNo ?? "未発行"}</span>
          </DocField>
          {cur.projectGroup ? (
            <DocField label={t("{project}")}>
              <Link to={`/groups/${cur.projectGroup.id}`} data-testid="dn-group">{cur.projectGroup.name} ›</Link>
            </DocField>
          ) : null}
          {/* 元の見積書（{acceptance}は{deliveryNote}から作ったときは元の{deliveryNote}だけ出す） */}
          {cur.sourceQuote && !(isAc && cur.sourceNote) ? (
            <DocField label={DELIVERY_NOTE_MSG.source}>
              <Link to={`/estimates/${cur.sourceQuote.id}`} data-testid="dn-source-quote">{`見積書 ${cur.sourceQuote.displayNo ?? ""}`.trim()} ›</Link>
            </DocField>
          ) : null}
          {cur.sourceNote ? (
            <DocField label={DELIVERY_NOTE_MSG.source}>
              <Link to={`/delivery-notes/${cur.sourceNote.id}`} data-testid="dn-source-note">{`${cur.names.deliveryNote} ${cur.sourceNote.displayNo ?? ""}`.trim()} ›</Link>
            </DocField>
          ) : null}
          {!isAc && cur.acceptances.length ? (
            <DocField label={title === cur.names.deliveryNote ? (cur.names.acceptance || t("{acceptance}")) : t("{acceptance}")}>
              <span data-testid="dn-acceptances">
                {cur.acceptances.map((x, i) => (
                  <span key={x.id}>
                    {i ? "・" : ""}
                    <Link to={`/acceptances/${x.id}`}>{x.displayNo ?? "下書き"} ›</Link>
                  </span>
                ))}
              </span>
            </DocField>
          ) : null}
          {/* スイッチ「{acceptance}」（3段目＝この1枚。使う機能「{acceptance}」の会社だけ・既定は{customer}の設定。Figma ⑥-3） */}
          {!isAc && usesAc && sw && !phone ? (
            <span className="dh-switch" data-testid="dn-acceptance-switch-wrap">
              <Switch checked={swOn} onChange={(v) => void setSwitch(v)} label={cur.names.acceptance || t("{acceptance}")} testId="dn-acceptance-switch" />
              <Help label={t(ACCEPTANCE_MSG.switch)} text={t(ACCEPTANCE_MSG.switchHelp)} testId="help-dn-acceptance-switch" />
            </span>
          ) : null}
        </>
      ),
    },
    chips: (
      <>
        {stateChip(cur)}
        {cur.taxOn === "invoice" && !isAc ? (
          <span className="chip quiet" data-testid="dn-tax-on" title={t(DELIVERY_NOTE_MSG.taxOnHelp)}>
            {DELIVERY_NOTE_MSG.taxOnInvoice}
          </span>
        ) : null}
      </>
    ),
    help: [
      // #276：見出しのチップ（番号ありの下書き）の title と同じ1行
      cur.status === "draft" && cur.documentNo ? `${unissuedTitle(cur.documentNo)}。` : null,
      isAc
        ? t("{acceptance}は税抜の合計だけを印字します（消費税は書きません）。紙の下に検収欄（検収日・検収者・印）が付きます。")
        : cur.taxOn === "invoice"
          ? t("消費税は請求書で計算します（{customer}の設定「税を確定する書類」が請求書）。紙には税抜の金額だけを印字します。")
          : t("この{customer}は{deliveryNote}で消費税を確定します（紙に税率ごとの消費税を印字します）。"),
      cur.sourceQuote && !isAc ? t("見積の残より多い数量は発行できません。") : null,
      phone ? t("スマホでは閲覧のみです。修正はパソコンで行います。") : null,
    ],
    head: () => <ErrorText>{hq.state.kind === "error" ? hq.state.msg : undefined}</ErrorText>,
    footer: (
      <>
        <div className="paper memo">
          <div className="field">
            <label htmlFor="dn_memo">備考（紙の下に印字されます）</label>
            <textarea id="dn_memo" value={head.notes} readOnly={readOnly} onChange={(e) => setH({ notes: e.target.value })} />
          </div>
        </div>
      </>
    ),
  };

  return (
    <>
      <Editor initial={initial} invoice={backend} />
      {hq.dialog}
      {dlg ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !dlg.busy && setDlg(null)}>
          <div className="dlg" role="dialog" aria-label={DELIVERY_NOTE_MSG.issueTitle} onKeyDown={(e) => e.key === "Escape" && !dlg.busy && setDlg(null)} data-testid="dn-issue-dialog">
            <div className="dh">{DELIVERY_NOTE_MSG.issueTitle}</div>
            <div className="db">
              <p className="muted" style={{ margin: 0 }}>
                {title}　{cur.client.name} {cur.client.honorific || "御中"}　{cur.taxOn === "invoice" ? `合計（税抜） ${yen(cur.revision.subtotal + cur.revision.reimbursementTotal)}円` : `合計 ${yen(cur.revision.total)}円`}
              </p>
              {/* #278：番号のある下書き（発行を取消した）は、同じ番号で再発行することを本文の1行で */}
              {cur.documentNo ? <p style={{ margin: 0 }} data-testid="issue-reissue">{unissuedReissue(cur.documentNo)}</p> : null}
              {cur.taxOn === "invoice" ? <p className="hint" style={{ margin: 0 }} data-testid="dn-issue-tax">{DELIVERY_NOTE_MSG.taxOnInvoice}</p> : null}
              {/* 締めに入れない（#267、docs/97 決めごと7 ②）：{project}・工事・{customer}が締めのときだけ。押すとこの1枚だけ都度で請求する */}
              {cur.billing?.closingClient ? (
                <label className="row" style={{ gap: 8, alignItems: "center" }}>
                  <input type="checkbox" checked={!!dlg.separate} onChange={(e) => setDlg({ ...dlg, separate: e.target.checked })} data-testid="dn-issue-separate" />
                  {CLOSING_MSG.noClosing}
                  <Help testId="help-dn-separate" label={CLOSING_MSG.noClosing} text={t(CLOSING_MSG.noClosingHelp)} />
                </label>
              ) : null}
              {needsCompany ? (
                <Field id="dni_co" label="会社名（屋号・お名前でも）">
                  <input id="dni_co" className="inp" autoComplete="organization" value={dlg.company} onChange={(e) => setDlg({ ...dlg, company: e.target.value, error: undefined })} />
                </Field>
              ) : null}
              <ErrorText>{dlg.error}</ErrorText>
            </div>
            <div className="df">
              <span className="sp" />
              <button type="button" className="btn quiet" disabled={dlg.busy} onClick={() => setDlg(null)}>取消</button>
              <button type="button" className="btn primary" disabled={dlg.busy} onClick={() => void doIssue()} data-testid="dn-issue-confirm">
                {DELIVERY_NOTE_MSG.issue}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {askDelete ? <DeleteDraftDialog prefix="dn" onClose={() => setAskDelete(false)} onConfirm={deleteDraft} /> : null}
      {paperDlg ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !paperDlg.busy && setPaperDlg(null)}>
          <div className="dlg" role="dialog" aria-label={isAc ? ACCEPTANCE_MSG.paperAccept : ACCEPTANCE_MSG.paperReceive} onKeyDown={(e) => e.key === "Escape" && !paperDlg.busy && setPaperDlg(null)} data-testid="dn-paper-dialog">
            <div className="dh">{isAc ? ACCEPTANCE_MSG.paperAccept : ACCEPTANCE_MSG.paperReceive}</div>
            <div className="db">
              <Field id="dnp_on" label={isAc ? ACCEPTANCE_MSG.acceptedOn : ACCEPTANCE_MSG.receivedOn}>
                <DateInput id="dnp_on" label={isAc ? ACCEPTANCE_MSG.acceptedOn : ACCEPTANCE_MSG.receivedOn} max={todayJst()} clearable={false} value={paperDlg.on} onChange={(v) => setPaperDlg({ ...paperDlg, on: v ?? "", error: undefined })} testId="dn-paper-on" />
              </Field>
              {isAc ? (
                <Field id="dnp_by" label={ACCEPTANCE_MSG.acceptedBy}>
                  <input id="dnp_by" className="inp" value={paperDlg.by} maxLength={60} onChange={(e) => setPaperDlg({ ...paperDlg, by: e.target.value })} data-testid="dn-paper-by" />
                </Field>
              ) : null}
              <ErrorText>{paperDlg.error}</ErrorText>
            </div>
            <div className="df">
              <span className="sp" />
              <button type="button" className="btn" disabled={paperDlg.busy} onClick={() => setPaperDlg(null)} data-testid="dn-paper-cancel">取消</button>
              <button type="button" className="btn primary" disabled={paperDlg.busy} onClick={() => void doPaper()} data-testid="dn-paper-confirm">決定</button>
            </div>
          </div>
        </div>
      ) : null}
      {unissueDlg ? <UnissueDialog doc="delivery_note" no={initial.displayNo ?? initial.documentNo ?? ""} busy={unissueDlg.busy} error={unissueDlg.error} onClose={() => setUnissueDlg(null)} onConfirm={() => void doUnissue()} /> : null}
      {bundle ? <BundleInvoiceDialog seed={cur} onClose={() => setBundle(false)} /> : null}
      {toastNode}
    </>
  );
}
